import React, { useState, useEffect, useReducer } from "react";
import { useAgentApi } from "../../services/AgentApi";
import AgentTable from "./AgentTable";
import AgentFilter, { searchByAll } from "./AgentFilter";
import Heading from "carbon-react/lib/components/heading";
import ErrorMessage from "../messages/ErrorMessage";
import AgentInviteDialog from "./agentInviteDialog/AgentInviteDialog";
import { Row, Column } from "carbon-react/lib/components/row";
import Button from "carbon-react/lib/components/button";
import CommentsDialog from "../common/CommentsDialog";
import AgentChangeRoleDialog from "./AgentChangeRoleDialog";
import {
  agentFieldNames,
  psuedoAgentStatusInvitePending,
  agentDocType,
} from "../../constants/model";
import {
  getEmptyPageMetadata,
  getCurrentPageTokenForPageRead,
  getPreviousPageTokenForPageRead,
  pagingReducer,
} from "../../utils/pageMetadataHelper";

import "./Agents.css";

const Agents = () => {
  const pagingDefaults = {
    pageSize: 15,
  };

  const [loading, setLoading] = useState(false);
  const [searchBy, setSearchBy] = useState(searchByAll);
  const [searchValue, setSearchValue] = useState("");
  const [cachedAgentPageSize, setCachedAgentPageSize] = useState(
    pagingDefaults.pageSize
  );
  const [pageMetadata, dispatchPageMetadata] = useReducer(
    pagingReducer,
    getEmptyPageMetadata()
  );
  const [agentResultSet, setAgentResultSet] = useState({
    agents: [],
    pageSize: cachedAgentPageSize,
  });
  const [fetchAgentsFailed, setFetchAgentsFailed] = useState(false);
  const [fetchErrorMsg, setFetchErrorMsg] = useState("");
  const { fetchAgents } = useAgentApi();
  const [showAgentInviteDialog, setShowAgentInviteDialog] = useState(false);
  const [commentsDialogState, setCommentsDialogState] = useState(null);
  const [commentsDialogOpen, setCommentsDialogOpen] = useState(false);
  const [showAgentChangeRoleDialog, setShowAgentChangeRoleDialog] = useState(
    false
  );
  const [selectedAgent, setSelectedAgent] = useState(null);

  useEffect(() => {
    getAgents();
  }, []); // eslint-disable-line

  const getAgents = async (
    _searchBy = searchBy,
    _searchValue = searchValue,
    pageSize = cachedAgentPageSize,
    continuationToken = null
  ) => {
    try {
      setLoading(true);
      setFetchErrorMsg("");
      setFetchAgentsFailed(false);

      switch (_searchBy) {
        case searchByAll:
          _searchBy = null;
          _searchValue = null;
          break;
        case agentFieldNames.email:
          if (_searchValue && _searchValue.length < 0) _searchValue = null;
          break;
        case agentFieldNames.status:
          if (_searchValue === psuedoAgentStatusInvitePending.value) {
            _searchBy = agentFieldNames.docType;
            _searchValue = agentDocType.invite;
          }
          break;
        default:
      }

      const apiResult = await fetchAgents(
        _searchBy,
        _searchValue,
        pageSize,
        continuationToken
      );

      if (!apiResult?.success || !apiResult?.data)
        throw new Error(apiResult?.error?.description || "Unexpected response");

      const resultSet = {
        agents: apiResult.data.agents || [],
        pageSize: pageSize,
      };

      dispatchPageMetadata({
        type: "ON_PAGE_READ_SUCCESS",
        currentPageToken: continuationToken,
        nextPageToken: apiResult.data.forwardToken,
      });

      setAgentResultSet(resultSet);
    } catch (error) {
      setFetchErrorMsg(error?.toString() || "The operation failed");
      setFetchAgentsFailed(true);
    }
    setLoading(false);
  };

  const onRefreshCurrentPage = async () => {
    const currentPageToken = getCurrentPageTokenForPageRead(
      pageMetadata,
      dispatchPageMetadata
    );

    await getAgents(
      searchBy,
      searchValue,
      cachedAgentPageSize,
      currentPageToken
    );
  };

  const onPagePrev = async () => {
    const prevPageToken = getPreviousPageTokenForPageRead(
      pageMetadata,
      dispatchPageMetadata
    );
    await getAgents(searchBy, searchValue, cachedAgentPageSize, prevPageToken);
  };

  const onPageNext = async () => {
    if (!pageMetadata?.nextPageToken || pageMetadata.nextPageToken.length < 1) {
      return;
    }

    await getAgents(
      searchBy,
      searchValue,
      cachedAgentPageSize,
      pageMetadata.nextPageToken
    );
  };

  const onAgentRemoved = async () => {
    await onRefreshCurrentPage();
  };

  const onSearchAgents = async (overrideSearchBy, overrideSearchValue) => {
    return !overrideSearchBy && !overrideSearchValue
      ? await getAgents()
      : await getAgents(overrideSearchBy, overrideSearchValue);
  };

  const onAgentUpdated = (updatedAgent) => {
    const index = agentResultSet.agents.findIndex(
      (agent) => agent.id === updatedAgent.id
    );

    agentResultSet.agents[index] = updatedAgent;
    setAgentResultSet({ ...agentResultSet });
  };

  const onInvitesCreated = async () => {
    // One or more invites created so refresh current
    // page to make invites visible (where possible)
    await onRefreshCurrentPage();
  };

  const onOpenCommentsDialog = (dialogState) => {
    setCommentsDialogState(dialogState);
    setCommentsDialogOpen(true);
  };

  const onCommentsDialogClose = () => {
    setCommentsDialogState(null);
    setCommentsDialogOpen(false);
  };

  const onChangeRoleDialogClose = () => {
    setShowAgentChangeRoleDialog(false);
  };

  const onOpenChangeRoleDialog = (agent) => {
    setSelectedAgent(agent);
    if (agent) setShowAgentChangeRoleDialog(true);
  };

  const headerRow = (
    <Row className="agents-header-row">
      <Column
        className="agents-header-column"
        columnAlign="left"
        columnOffset="0"
      >
        <Heading
          title="Manage agents"
          divider={false}
          className="manage-agents-heading"
        />
      </Column>
      <Column
        className="agents-header-column"
        columnAlign="right"
        columnSpan="1"
      >
        <Button
          id="invite-agents-btn"
          iconType="add"
          iconPosition="before"
          as="primary"
          onClick={() => {
            setShowAgentInviteDialog(true);
          }}
        >
          Invite Agents
        </Button>
      </Column>
    </Row>
  );

  return (
    <>
      {headerRow}
      <AgentFilter
        searchBy={searchBy}
        onSearchByChanged={setSearchBy}
        searchValue={searchValue}
        onSearchValueChanged={setSearchValue}
        onSearch={onSearchAgents}
      />
      {fetchAgentsFailed === true ? (
        <ErrorMessage
          message={
            fetchErrorMsg && fetchErrorMsg.length > 0
              ? fetchErrorMsg
              : "Failed to read agent data"
          }
        />
      ) : (
        <AgentTable
          agentResultSet={agentResultSet}
          loading={loading}
          onAgentUpdated={onAgentUpdated}
          onAgentRemoved={onAgentRemoved}
          onOpenCommentsDialog={onOpenCommentsDialog}
          onOpenChangeRoleDialog={onOpenChangeRoleDialog}
          onPagePrev={onPagePrev}
          onPageNext={onPageNext}
          pageNo={pageMetadata.previousPageTokens.length + 1}
          lastPage={!pageMetadata.nextPageToken}
        />
      )}
      <AgentInviteDialog
        open={showAgentInviteDialog}
        onInvitesCreated={onInvitesCreated}
        onClose={() => setShowAgentInviteDialog(false)}
      />
      <CommentsDialog
        dialogState={commentsDialogState}
        open={commentsDialogOpen}
        onClose={onCommentsDialogClose}
      />
      <AgentChangeRoleDialog
        open={showAgentChangeRoleDialog}
        agent={selectedAgent}
        onAgentUpdated={onAgentUpdated}
        onClose={onChangeRoleDialogClose}
      />
    </>
  );
};

export default Agents;
